import { AttendanceForm } from "@/forms/attendance-form";
import { client } from "@/hono";
import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { type EditAttendanceFormSchema, editAttendanceFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditAttendanceModalProps extends ModalProps {
	userId: string;
	attendanceId: string;
	onComplete: (() => Promise<void>) | undefined;
}

export function EditAttendanceModal({
	isOpen,
	onClose,
	userId,
	attendanceId,
	onComplete,
}: EditAttendanceModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.users[":userId"].attendances[":attendanceId"].$get({
			param: { userId, attendanceId },
		});
		const data = await responseHandler(response);
		if (!data.attendance) throw new Error();

		return editAttendanceFormSchema.parse({
			startTime: moment(data.attendance?.startTime).getTime(),
			endTime: data.attendance?.endTime ? moment(data.attendance?.endTime).getTime() : null,
			worksite: data.attendance?.worksite,
			projectId: data.attendance?.projectId,
		}) as EditAttendanceFormSchema;
	});
	const methods = useForm({
		resolver: resolver(editAttendanceFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: EditAttendanceFormSchema) {
		const response = await client.users[":userId"].attendances[":attendanceId"].$patch({
			param: { attendanceId, userId },
			json,
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("users.edit_attendance")}
				onClose={onClose}
				isOpen={isOpen}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<AttendanceForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
